import { useAtomValue, useSetAtom } from 'jotai';
import { Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { renderingsSelectedImageAtom } from '@/features/renderings/shared/atoms/image-files';
import {
  renderingsEstateIdAtom,
  renderingsProjectIdAtom,
} from '@/features/renderings/shared/atoms/relation';

import { magicEditModeAtom } from '../atoms/mode';
import { MagicEditInstructions } from './magic-edit-instructions';

type Props = {
  onCreate: () => void;
  isCreating?: boolean;
  isMobileView?: boolean;
  onContinue?: () => void;
};

export const MagicEditTab = ({ onCreate, isCreating, isMobileView, onContinue }: Props) => {
  const { t } = useTranslation();
  const setMode = useSetAtom(magicEditModeAtom);
  const selectedImage = useAtomValue(renderingsSelectedImageAtom);
  const estateId = useAtomValue(renderingsEstateIdAtom);
  const projectId = useAtomValue(renderingsProjectIdAtom);

  const hasSelectedObject = Boolean(estateId || projectId);
  const canContinue = Boolean(selectedImage);
  const showContinue = Boolean(isMobileView);

  const handleContinue = () => {
    if (!selectedImage) return;

    if (!hasSelectedObject) {
      toast.warning(t('renderings.select-estate-or-project'));
      return;
    }

    onContinue?.();
  };

  useEffect(() => {
    setMode('free-edit');
  }, [setMode]);

  return (
    <div className="flex min-h-full w-full flex-col sm:w-[287px]">
      <MagicEditInstructions className="sm:w-[287px]" />

      <div className="sticky bottom-0 z-20 mt-auto bg-white pt-2">
        <div className="flex w-full justify-center">
          {showContinue ? (
            <Button
              type="button"
              className="h-11 w-full gap-3 rounded-sm text-base font-semibold"
              color="primary-light"
              variant="contained"
              disabled={!canContinue}
              onClick={handleContinue}
            >
              <Sparkles aria-hidden className="size-5" />
              {t('continue')}
            </Button>
          ) : (
            <Button
              type="button"
              className="h-11 w-full gap-3 rounded-sm text-base font-semibold"
              color="primary-light"
              variant="contained"
              disabled={Boolean(isCreating)}
              onClick={onCreate}
            >
              <Sparkles aria-hidden className="size-5" />
              {t('create')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
